import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { ClipboardList, Download, ChevronDown, ChevronRight, FileText, AlertTriangle } from 'lucide-react';
import api from '../../utils/api';
import ReasonBadge from '../../components/ReasonBadge';
import { fmtTime12, isoDate } from '../../utils/time';
import {
  KpiTile, Tabs, EmptyState, ErrorState, TableSkeleton, StatusPill, PriorityPill, fmtTimeRange, humanise, num, plural,
} from '../../components/ui';
import StaffHeader from './StaffHeader';
import { useStaffRecord, toDay } from './hrKit';

const dayLong = (v) => { const d = toDay(v); return d ? d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : '—'; };

function calcHours(start, end) {
  if (!start || !end) return 0;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  let mins = (eh * 60 + em) - (sh * 60 + sm);
  if (mins < 0) mins += 1440;
  return parseFloat((mins / 60).toFixed(2));
}

// Local calendar dates, so "this month" never starts on the last day of the previous month.
function getDateRange(preset) {
  const now = new Date();
  if (preset === 'this_week') {
    const day = now.getDay();
    const mon = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (day === 0 ? 6 : day - 1));
    const sun = new Date(mon.getFullYear(), mon.getMonth(), mon.getDate() + 6);
    return { from: isoDate(mon), to: isoDate(sun) };
  }
  if (preset === 'this_month') {
    return { from: isoDate(new Date(now.getFullYear(), now.getMonth(), 1)), to: isoDate(new Date(now.getFullYear(), now.getMonth() + 1, 0)) };
  }
  return { from: '', to: '' };
}

function exportToCSV(shifts, staffName) {
  const header = ['Date', 'Shift Type', 'Clients', 'Start', 'End', 'Hours', 'Status'];
  const rows = shifts.map((s) => [
    s.shift_date, s.shift_type || '', s.client_names || '',
    s.start_time ? fmtTime12(s.start_time) : '', s.end_time ? fmtTime12(s.end_time) : '',
    calcHours(s.start_time, s.end_time), s.status || '',
  ]);
  const csv = [header, ...rows].map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${staffName?.replace(/\s+/g, '_') || 'staff'}_shifts.csv`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function ShiftDetailPanel({ staffId, shiftId }) {
  const [notes, setNotes] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get(`/staff/${staffId}/progress-notes?shift_id=${shiftId}`),
      api.get(`/staff/${staffId}/client-incidents?shift_id=${shiftId}`),
    ]).then(([n, i]) => { if (alive) { setNotes(n.data || []); setIncidents(i.data || []); } })
      .catch(() => { if (alive) setFailed(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [staffId, shiftId]);

  if (loading) return <div className="space-y-2" aria-busy="true"><div className="skeleton h-4 w-40" /><div className="skeleton h-12 w-full" /></div>;
  if (failed) return <p className="text-[13px] text-rose-700">We couldn't load this shift's notes and incidents.</p>;
  if (!notes.length && !incidents.length) return <p className="text-[13px] text-slate-500">No progress notes or incidents recorded for this shift.</p>;

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <div>
        <h4 className="mb-2 flex items-center gap-1.5 text-[12px] font-semibold text-slate-500"><FileText size={12} aria-hidden="true" />Progress notes ({notes.length})</h4>
        {notes.length === 0 ? <p className="text-[13px] text-slate-500">None recorded.</p> : (
          <ul className="space-y-2">
            {notes.map((n) => (
              <li key={n.id} className="rounded-lg border border-slate-200 bg-white p-3">
                <div className="mb-1 flex flex-wrap items-center gap-1.5">
                  {n.note_type && <span className="badge-gray">{n.note_type}</span>}
                  {n.client_name && <span className="text-[12.5px] text-slate-600">{n.client_name}</span>}
                </div>
                <p className="line-clamp-4 whitespace-pre-wrap text-[13px] leading-relaxed text-slate-700">{n.note_content}</p>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div>
        <h4 className="mb-2 flex items-center gap-1.5 text-[12px] font-semibold text-slate-500"><AlertTriangle size={12} aria-hidden="true" />Incidents ({incidents.length})</h4>
        {incidents.length === 0 ? <p className="text-[13px] text-slate-500">None recorded.</p> : (
          <ul className="space-y-2">
            {incidents.map((inc) => (
              <li key={inc.id} className="rounded-lg border border-slate-200 bg-white p-3">
                <div className="mb-1 flex flex-wrap items-center gap-1.5">
                  <PriorityPill priority={inc.priority || 'medium'} />
                  <span className="text-[12.5px] text-slate-700">{humanise(inc.incident_type) || 'Incident'}</span>
                  {inc.client_name && <span className="text-[12.5px] text-slate-500">· {inc.client_name}</span>}
                </div>
                {inc.title && <p className="text-[13px] font-medium text-slate-800">{inc.title}</p>}
                {inc.description && <p className="line-clamp-3 whitespace-pre-wrap text-[13px] leading-relaxed text-slate-700">{inc.description}</p>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default function StaffEmployeeShifts() {
  const { id } = useParams();
  const { staff, error: staffError, reload: reloadStaff } = useStaffRecord(id);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [preset, setPreset] = useState('this_month');
  const [dateRange, setDateRange] = useState(getDateRange('this_month'));
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(() => {
    const params = new URLSearchParams({ staff_id: id });
    if (dateRange.from) params.set('start_date', dateRange.from);
    if (dateRange.to) params.set('end_date', dateRange.to);
    setLoading(true); setLoadError(null);
    api.get(`/shifts?${params}`)
      .then((r) => setShifts(r.data?.shifts || (Array.isArray(r.data) ? r.data : [])))
      .catch((e) => setLoadError(e.response?.data?.message || "We couldn't load shifts. Try again."))
      .finally(() => setLoading(false));
  }, [id, dateRange.from, dateRange.to]);
  useEffect(() => { load(); }, [load]);

  const applyPreset = (p) => { setPreset(p); setExpandedId(null); if (p !== 'custom') setDateRange(getDateRange(p)); };
  const handleCustomDate = (key, val) => { setDateRange((r) => ({ ...r, [key]: val })); setPreset('custom'); setExpandedId(null); };

  const totalHours = shifts.reduce((sum, s) => sum + calcHours(s.start_time, s.end_time), 0);
  const completedHours = shifts.filter((s) => s.status === 'completed').reduce((sum, s) => sum + calcHours(s.start_time, s.end_time), 0);
  const sorted = [...shifts].sort((a, b) => String(b.shift_date || '').localeCompare(String(a.shift_date || '')));

  return (
    <div>
      <StaffHeader staff={staff} error={staffError} onRetry={reloadStaff} />
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <KpiTile label="Shifts" value={loading ? '…' : num(shifts.length)} accent="slate" />
          <KpiTile label="Rostered hours" value={loading ? '…' : `${num(totalHours, 1)} h`} accent="slate" />
          <KpiTile label="Completed hours" value={loading ? '…' : `${num(completedHours, 1)} h`} accent="emerald" />
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Tabs variant="segmented" size="sm" ariaLabel="Date range" value={preset} onChange={applyPreset}
            tabs={[{ id: 'this_week', label: 'This week' }, { id: 'this_month', label: 'This month' }, { id: 'custom', label: 'Custom' }]} />
          {preset === 'custom' && (
            <div className="flex flex-wrap items-center gap-2">
              <input type="date" aria-label="From" value={dateRange.from} onChange={(e) => handleCustomDate('from', e.target.value)} className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-[13px]" />
              <span className="text-slate-400" aria-hidden="true">–</span>
              <input type="date" aria-label="To" value={dateRange.to} onChange={(e) => handleCustomDate('to', e.target.value)} className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-[13px]" />
            </div>
          )}
          <button type="button" onClick={() => exportToCSV(sorted, staff ? `${staff.first_name}_${staff.last_name}` : 'staff')} disabled={!shifts.length} className="btn-secondary btn-sm ml-auto">
            <Download size={13} aria-hidden="true" />Export CSV
          </button>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white">
          {loading ? <TableSkeleton rows={5} cols={5} />
            : loadError ? <ErrorState message={loadError} onRetry={load} />
              : sorted.length === 0 ? <EmptyState icon={ClipboardList} title="No shifts in this period" hint="Try another date range." />
                : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[720px] text-[13px]">
                      <thead>
                        <tr className="border-b border-slate-200 text-left text-[12px] text-slate-500">
                          <th className="w-10 px-3 py-2"><span className="sr-only">Details</span></th>
                          <th className="px-3 py-2 font-medium">Date</th><th className="px-3 py-2 font-medium">Type</th>
                          <th className="px-3 py-2 font-medium">Clients</th><th className="px-3 py-2 font-medium">Time</th>
                          <th className="px-3 py-2 text-right font-medium">Hours</th><th className="px-3 py-2 font-medium">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sorted.map((shift) => {
                          const open = expandedId === shift.id;
                          return (
                            <React.Fragment key={shift.id}>
                              <tr className="border-b border-slate-100 hover:bg-slate-50">
                                <td className="px-3 py-2">
                                  <button type="button" onClick={() => setExpandedId(open ? null : shift.id)} aria-expanded={open}
                                    aria-label={`${open ? 'Hide' : 'Show'} notes and incidents for ${dayLong(shift.shift_date)}`}
                                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100">
                                    {open ? <ChevronDown size={15} aria-hidden="true" /> : <ChevronRight size={15} aria-hidden="true" />}
                                  </button>
                                </td>
                                <td className="whitespace-nowrap px-3 py-2 font-medium text-slate-800">{dayLong(shift.shift_date)}</td>
                                <td className="px-3 py-2 text-slate-600">{shift.shift_type ? humanise(shift.shift_type) : 'Standard'}</td>
                                <td className="max-w-[200px] truncate px-3 py-2 text-slate-700" title={shift.client_names || ''}>{shift.client_names || '—'}</td>
                                <td className="whitespace-nowrap px-3 py-2 text-slate-600">{fmtTimeRange(shift.start_time, shift.end_time)}</td>
                                <td className="px-3 py-2 text-right tabular-nums text-slate-800">{num(calcHours(shift.start_time, shift.end_time), 2)}</td>
                                <td className="px-3 py-2"><span className="flex flex-wrap items-center gap-1.5"><StatusPill status={shift.status || 'booked'} /><ReasonBadge shift={shift} /></span></td>
                              </tr>
                              {open && (
                                <tr className="border-b border-slate-200 bg-slate-50/70">
                                  <td colSpan={7} className="px-6 py-4"><ShiftDetailPanel staffId={id} shiftId={shift.id} /></td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr className="border-t border-slate-200 bg-slate-50">
                          <td />
                          <td colSpan={4} className="px-3 py-2.5 text-[12.5px] font-medium text-slate-600">{plural(sorted.length, 'shift')}</td>
                          <td className="px-3 py-2.5 text-right font-semibold tabular-nums text-slate-900">{num(totalHours, 1)} h</td>
                          <td />
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
        </div>
      </div>
    </div>
  );
}
